import type { SunmiReceiptLine } from '../native/sunmi-printer';

type ReceiptOrderType = 'DELIVERY' | 'COLLECTION';

interface ReceiptItemExtra {
  name: string;
  quantity: number;
  price: number;
}

interface ReceiptItem {
  name: string;
  quantity: number;
  price: number;
  note: string;
  extras: ReceiptItemExtra[];
}

interface ReceiptModel {
  tenantName: string;
  companyNumber: string | null;
  publicCode: string;
  created: string;
  orderType: ReceiptOrderType;
  isPaid: boolean;
  customerName: string;
  phone: string;
  delivery: any;
  items: ReceiptItem[];
  subtotal: number;
  deliveryFeeGross: number;
  customerServiceFeeGross: number;
  smallOrderFee: number;
  total: number;
  comment: string;
}

function decodeHtmlEntities(value: any): string {
  return String(value ?? '')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'");
}

function cleanText(value: any): string {
  return decodeHtmlEntities(value).trim();
}

function money(v: any): string {
  const n = Number(v ?? 0);
  if (Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
}

function moneyNumber(v: any): number {
  const n = Number(v ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function line(label: string, value: string, width = 28): string {
  // Preserve leading spaces for indentation in the label
  const decodedLabel = decodeHtmlEntities(label);
  const indentMatch = decodedLabel.match(/^ +/);
  const indent = indentMatch ? indentMatch[0] : '';

  const left = indent + decodedLabel.trim();
  const right = decodeHtmlEntities(value).trim();

  const spaceCount = Math.max(1, width - left.length - right.length);
  return left + ' '.repeat(spaceCount) + right;
}

function divider(width = 28): string {
  return '-'.repeat(width);
}

function safeUpper(v: any): string {
  return cleanText(v).toUpperCase();
}

function wrapText(text: string, width = 28): string[] {
  const decoded = decodeHtmlEntities(text);

  // Capture leading spaces for indentation
  const indentMatch = decoded.match(/^ +/);
  const indent = indentMatch ? indentMatch[0] : '';

  // Get content without outer whitespace for wrapping logic
  const content = decoded.trim();
  if (!content) return [];

  // Split into words but preserve the indentation for the start of every wrapped line
  const words = content.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let currentLine = indent + words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    if ((currentLine + ' ' + word).length <= width) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = indent + word; // Carry indentation to the next line
    }
  }

  lines.push(currentLine);
  return lines;
}

function buildReceiptModel(opts: {
  tenantName: string;
  companyNumber?: string | null;
  details: any;
}): ReceiptModel {
  const { tenantName, companyNumber, details } = opts;

  const rawItems: any[] = Array.isArray(details?.details)
    ? details.details
    : [];

  const items: ReceiptItem[] = rawItems.map((it) => {
    const extras: ReceiptItemExtra[] = Array.isArray(it?.extras)
      ? it.extras.map((ex: any) => ({
          name: cleanText(ex?.extraName ?? 'Extra'),
          quantity: moneyNumber(ex?.extraQuantity ?? 1),
          price: moneyNumber(ex?.extraPrice ?? 0),
        }))
      : [];

    return {
      name: cleanText(it?.itemName ?? 'Item'),
      quantity: moneyNumber(it?.itemQuantity ?? 1),
      price: moneyNumber(
        it?.itemTotalGrossModified ??
          it?.itemTotalGrossOriginal ??
          it?.itemBasePrice ??
          0,
      ),
      note: cleanText(it?.itemNote ?? ''),
      extras,
    };
  });

  const deliveryFeeGross = moneyNumber(
    details?.deliveryFeeGross ??
      details?.deliveryFeeOriginalGross ??
      details?.delivery_fee_original_gross ??
      0,
  );

  const customerServiceFeeGross = moneyNumber(
    details?.customerServiceFeeGross ??
      details?.customer_service_fee_gross ??
      details?.orderFeeOriginalGross ??
      details?.order_fee_original_gross ??
      0,
  );

  const smallOrderFee = moneyNumber(details?.smallOrderFee ?? 0);

  const total = moneyNumber(
    details?.orderTotalCurrentGross ??
      details?.total ??
      details?.totalPrice ??
      0,
  );

  const calculatedItemsSubtotal = items.reduce(
    (sum, item) => sum + item.price,
    0,
  );

  const explicitSubtotal =
    details?.subTotal ??
    details?.subtotal ??
    details?.totalBeforeFees ??
    details?.totalBeforeFeesPrice ??
    null;

  const subtotal =
    explicitSubtotal != null
      ? moneyNumber(explicitSubtotal)
      : total > 0
        ? total - deliveryFeeGross - customerServiceFeeGross - smallOrderFee
        : calculatedItemsSubtotal;

  const orderTypeRaw = String(details?.orderType ?? '').toLowerCase();
  const orderType: ReceiptOrderType =
    orderTypeRaw === 'collection' ? 'COLLECTION' : 'DELIVERY';

  const paid = String(details?.paymentStatus ?? '').toLowerCase();
  const isPaid = ['paid', 'captured', 'succeeded', 'partial_refund'].includes(
    paid,
  );

  return {
    tenantName: safeUpper(tenantName),
    companyNumber: companyNumber ? cleanText(companyNumber) : null,
    publicCode: cleanText(details?.publicOrderCode ?? ''),
    created: cleanText(
      details?.createdAtDisplay ??
        details?.createdAt ??
        details?.created ??
        details?.dateCreated ??
        '',
    ),
    orderType,
    isPaid,
    customerName: cleanText(details?.firstName ?? ''),
    phone: cleanText(details?.phoneNumber ?? ''),
    delivery: details?.delivery ?? null,
    items,
    subtotal,
    deliveryFeeGross,
    customerServiceFeeGross,
    smallOrderFee,
    total,
    comment: cleanText(details?.comment ?? ''),
  };
}

export function buildReceiptText(opts: {
  tenantName: string;
  companyNumber?: string | null;
  details: any;
  width?: number;
}): string {
  const width = opts.width ?? 28;
  const receipt = buildReceiptModel(opts);

  const lines: string[] = [];

  lines.push(receipt.tenantName);
  if (receipt.companyNumber) {
    lines.push(`Company No: ${receipt.companyNumber}`);
  }

  lines.push(divider(width));
  lines.push(`*** ${receipt.orderType} ***`);
  lines.push(divider(width));

  if (receipt.publicCode) lines.push(`Order Code: ${receipt.publicCode}`);
  if (receipt.created) lines.push(`Time: ${receipt.created}`);
  lines.push(`Paid: ${receipt.isPaid ? 'YES' : 'NO'}`);
  lines.push(divider(width));

  if (receipt.orderType === 'DELIVERY' && receipt.delivery) {
    lines.push('Delivery address:');

    if (receipt.delivery.address_line_1) {
      lines.push(...wrapText(receipt.delivery.address_line_1, width));
    }

    if (receipt.delivery.address_line_2) {
      lines.push(...wrapText(receipt.delivery.address_line_2, width));
    }

    const cityLine = [receipt.delivery.city, receipt.delivery.county]
      .filter(Boolean)
      .map(cleanText)
      .join(', ');

    if (cityLine) lines.push(...wrapText(cityLine, width));

    const postLine = [receipt.delivery.country, receipt.delivery.postal_code]
      .filter(Boolean)
      .map(cleanText)
      .join(' ');

    if (postLine) lines.push(...wrapText(postLine, width));

    lines.push(divider(width));
  }

  if (receipt.customerName) lines.push(`Customer: ${receipt.customerName}`);
  if (receipt.phone) lines.push(`Phone: ${receipt.phone}`);
  lines.push(divider(width));

  for (const item of receipt.items) {
    lines.push(...wrapText(`${item.quantity} x ${item.name}`, width));

    for (const extra of item.extras) {
      const extraName =
        extra.quantity > 1
          ? `    + ${extra.quantity} x ${extra.name}`
          : `    + ${extra.name}`;

      if (extra.price > 0) {
        lines.push(line(extraName, `+ £${money(extra.price)}`, width));
      } else {
        lines.push(...wrapText(extraName, width));
      }
    }

    if (item.note) {
      lines.push(...wrapText(`    Note: ${item.note}`, width));
    }

    lines.push(line('', `£${money(item.price)}`, width));
  }

  lines.push(divider(width));

  lines.push(line('Subtotal', `£${money(receipt.subtotal)}`, width));

  if (receipt.deliveryFeeGross > 0) {
    lines.push(
      line('Delivery fee', `£${money(receipt.deliveryFeeGross)}`, width),
    );
  }

  if (receipt.customerServiceFeeGross > 0) {
    lines.push(
      line('Service fee', `£${money(receipt.customerServiceFeeGross)}`, width),
    );
  }

  if (receipt.smallOrderFee > 0) {
    lines.push(
      line('Small order fee', `£${money(receipt.smallOrderFee)}`, width),
    );
  }

  lines.push(line('TOTAL', `£${money(receipt.total)}`, width));
  lines.push(divider(width));

  if (receipt.comment) {
    lines.push('Customer note:');
    lines.push(...wrapText(receipt.comment, width));
    lines.push(divider(width));
  }

  lines.push('Thank you!');
  lines.push('Powered by Waitron');
  lines.push('\n');

  return lines.join('\n');
}

export function buildReceiptLines(opts: {
  tenantName: string;
  companyNumber?: string | null;
  details: any;
  width?: number;
}): SunmiReceiptLine[] {
  const width = opts.width ?? 28;
  const receipt = buildReceiptModel(opts);

  const lines: SunmiReceiptLine[] = [];

  const SECTION_SPACE = 1;
  const SMALL_SPACE = 1;
  const ITEM_SPACE = 1;
  const FOOTER_SPACE = 2;

  const addText = (
    content: string,
    size = 24,
    bold = false,
    align: 'LEFT' | 'CENTER' | 'RIGHT' = 'LEFT',
  ) => {
    lines.push({
      type: 'text',
      content,
      size,
      bold,
      align,
    });
  };

  const addWrappedText = (
    content: string,
    size = 24,
    bold = false,
    align: 'LEFT' | 'CENTER' | 'RIGHT' = 'LEFT',
  ) => {
    for (const l of wrapText(content, width)) {
      addText(l, size, bold, align);
    }
  };

  const addDivider = () => {
    lines.push({ type: 'divider' });
  };

  const addSpace = (height = 1) => {
    lines.push({ type: 'space', height });
  };

  const addRow = (left: string, right: string, size = 24, bold = false) => {
    lines.push({
      type: 'row',
      columns: [
        { content: left, weight: 3, size, bold, align: 'LEFT' },
        { content: right, weight: 1, size, bold, align: 'RIGHT' },
      ],
    });
  };

  addSpace(SMALL_SPACE);
  addText(receipt.tenantName, 34, true, 'CENTER');

  if (receipt.companyNumber) {
    addText(`Company No: ${receipt.companyNumber}`, 22, false, 'CENTER');
  }

  addSpace(SMALL_SPACE);
  addDivider();
  addSpace(SECTION_SPACE);

  addText(`*** ${receipt.orderType} ***`, 34, true, 'CENTER');
  addSpace(SMALL_SPACE);
  addDivider();
  addSpace(SECTION_SPACE);

  if (receipt.publicCode)
    addText(`Order Code: ${receipt.publicCode}`, 24, true);
  if (receipt.created) addText(`Time: ${receipt.created}`, 22);
  addText(`Paid: ${receipt.isPaid ? 'YES' : 'NO'}`, 24, true);
  addSpace(SMALL_SPACE);
  addDivider();
  addSpace(SECTION_SPACE);

  if (receipt.orderType === 'DELIVERY' && receipt.delivery) {
    addText('Delivery address:', 24, true);

    if (receipt.delivery.address_line_1) {
      addWrappedText(receipt.delivery.address_line_1, 24);
    }

    if (receipt.delivery.address_line_2) {
      addWrappedText(receipt.delivery.address_line_2, 24);
    }

    const cityLine = [receipt.delivery.city, receipt.delivery.county]
      .filter(Boolean)
      .map(cleanText)
      .join(', ');

    if (cityLine) addWrappedText(cityLine, 24);

    const postLine = [receipt.delivery.country, receipt.delivery.postal_code]
      .filter(Boolean)
      .map(cleanText)
      .join(' ');

    if (postLine) addWrappedText(postLine, 24);

    addSpace(SMALL_SPACE);
    addDivider();
    addSpace(SECTION_SPACE);
  }

  if (receipt.customerName)
    addText(`Customer: ${receipt.customerName}`, 24, true);
  if (receipt.phone) addText(`Phone: ${receipt.phone}`, 24, true);
  addSpace(SMALL_SPACE);
  addDivider();
  addSpace(SECTION_SPACE);

  for (const item of receipt.items) {
    // Item name first
    addWrappedText(`${item.quantity} x ${item.name}`, 26, true);

    // Extras directly below item name
    for (const extra of item.extras) {
      const extraLabel =
        extra.quantity > 1
          ? `    + ${extra.quantity} x ${extra.name}`
          : `    + ${extra.name}`;

      if (extra.price > 0) {
        addText(line(extraLabel, `+ £${money(extra.price)}`, width), 22, false);
      } else {
        addWrappedText(extraLabel, 22, false);
      }
    }

    // Item note under extras
    if (item.note) {
      addWrappedText(`    Note: ${item.note}`, 22, false);
    }

    // Full item price after extras
    addRow('', `£${money(item.price)}`, 24, false);

    addSpace(ITEM_SPACE);
  }

  addDivider();
  addSpace(SMALL_SPACE);

  addRow('Subtotal', `£${money(receipt.subtotal)}`, 24);

  if (receipt.deliveryFeeGross > 0) {
    addRow('Delivery fee', `£${money(receipt.deliveryFeeGross)}`, 24);
  }

  if (receipt.customerServiceFeeGross > 0) {
    addRow('Service fee', `£${money(receipt.customerServiceFeeGross)}`, 24);
  }

  if (receipt.smallOrderFee > 0) {
    addRow('Small order fee', `£${money(receipt.smallOrderFee)}`, 24);
  }

  addSpace(SMALL_SPACE);
  addRow('TOTAL', `£${money(receipt.total)}`, 30, true);
  addSpace(SMALL_SPACE);
  addDivider();
  addSpace(SECTION_SPACE);

  if (receipt.comment) {
    addText('Customer note:', 24, true);
    addWrappedText(receipt.comment, 24);
    addSpace(SMALL_SPACE);
    addDivider();
    addSpace(SECTION_SPACE);
  }

  addText('Thank you!', 24, false, 'CENTER');
  addSpace(SMALL_SPACE);

  lines.push({
    type: 'image',
    assetName: 'waitron_logo_receipt',
    width: 190,
    align: 'CENTER',
  });

  addSpace(SMALL_SPACE);
  addText('Powered by waitron.uk', 20, false, 'CENTER');

  addSpace(FOOTER_SPACE);

  return lines;
}
